import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Navbar } from "../components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Progress } from "../components/ui/progress";
import { 
  FileBarChart, 
  Download,
  Eye,
  CheckCircle2,
  XCircle,
  Calendar,
  FileDown,
  Search,
  Filter,
  ArrowUpDown,
  AlertCircle,
  Video,
  Play,
  Loader2,
  TrendingUp
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Reports() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [verdictFilter, setVerdictFilter] = useState('all');
  const [jobFilter, setJobFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');

  useEffect(() => {
    fetchData();
    // Mark reports as viewed for badge auto-clear
    localStorage.setItem('ema_reports_last_viewed', new Date().toISOString());
    window.dispatchEvent(new Event('ema_reports_viewed'));
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, reportsRes, jobsRes] = await Promise.all([
        fetch(`${API}/api/auth/me`, { credentials: 'include' }),
        fetch(`${API}/api/reports`, { credentials: 'include' }),
        fetch(`${API}/api/jobs`, { credentials: 'include' })
      ]);

      if (userRes.ok) setUser(await userRes.json());
      if (reportsRes.ok) setReports(await reportsRes.json());
      if (jobsRes.ok) setJobs(await jobsRes.json());
    } catch (error) {
      console.error('Error:', error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  // Load video URL for a session
  const loadVideoUrl = async (sessionId) => {
    setVideoLoading(true);
    setVideoUrl(null);
    try {
      const response = await fetch(`${API}/api/interview/v2/video-url/${sessionId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Build full URL from relative path for video element
        const fullUrl = data.video_url?.startsWith('/') ? `${API}${data.video_url}` : data.video_url;
        setVideoUrl(fullUrl);
      }
    } catch (error) {
      console.error('Error loading video:', error);
    } finally {
      setVideoLoading(false);
    }
  };
  
  // Filtered and sorted reports
  const filteredReports = useMemo(() => {
    let result = [...reports];
    
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r => 
        r.candidate?.name?.toLowerCase().includes(q) ||
        r.job?.title?.toLowerCase().includes(q) ||
        r.candidate?.email?.toLowerCase().includes(q)
      );
    }
    
    // Job filter
    if (jobFilter !== 'all') {
      result = result.filter(r => r.job?.job_id === jobFilter || r.job_id === jobFilter);
    }
    
    // Verdict filter
    if (verdictFilter !== 'all') {
      result = result.filter(r => {
        const rec = r.recommendation;
        if (verdictFilter === 'go') return rec === 'go';
        if (verdictFilter === 'evaluate') return rec === 'evaluate';
        if (verdictFilter === 'no_go') return rec === 'no_go';
        return true;
      });
    }
    
    // Sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.completed_at || b.created_at) - new Date(a.completed_at || a.created_at);
        case 'date_asc':
          return new Date(a.completed_at || a.created_at) - new Date(b.completed_at || b.created_at);
        case 'score_desc':
          return (b.overall_score || 0) - (a.overall_score || 0);
        case 'score_asc':
          return (a.overall_score || 0) - (b.overall_score || 0);
        case 'name_asc':
          return (a.candidate?.name || '').localeCompare(b.candidate?.name || '');
        case 'job_asc':
          return (a.job?.title || '').localeCompare(b.job?.title || '');
        default:
          return 0;
      }
    });
    
    return result;
  }, [reports, searchQuery, verdictFilter, jobFilter, sortBy]);
  
  // Group reports by job for folder view
  const reportsByJob = useMemo(() => {
    const grouped = {};
    filteredReports.forEach(report => {
      const jobTitle = report.job?.title || 'Sans poste';
      if (!grouped[jobTitle]) {
        grouped[jobTitle] = [];
      }
      grouped[jobTitle].push(report);
    });
    return grouped;
  }, [filteredReports]);

  const viewReport = async (sessionId) => {
    setDetailLoading(true);
    setSelectedReport(sessionId);
    setVideoUrl(null);
    
    try {
      const response = await fetch(`${API}/api/reports/${sessionId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        setDetailData(await response.json());
        // Also load video URL
        loadVideoUrl(sessionId);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement du rapport");
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadPdf = async (sessionId) => {
    try {
      toast.info("Génération du PDF...");
      const response = await fetch(`${API}/api/reports/${sessionId}/pdf`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rapport_entretien_${sessionId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("PDF téléchargé !");
      } else {
        toast.error("Erreur lors du téléchargement");
      }
    } catch (error) {
      toast.error("Erreur lors du téléchargement du PDF");
    }
  };

  const getScoreColor = (score) => {
    if (!score) return 'text-ink-muted';
    if (score >= 75) return 'text-functional-success';
    if (score >= 50) return 'text-functional-warning';
    return 'text-functional-error';
  };

  const getScoreBarColor = (score) => {
    if (!score) return 'bg-zinc-300';
    if (score >= 75) return 'bg-functional-success';
    if (score >= 50) return 'bg-functional-warning';
    return 'bg-functional-error';
  };

  return (
    <div className="min-h-screen bg-canvas dark:bg-zinc-950">
      <Navbar user={user} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="font-display font-bold text-2xl sm:text-3xl text-ink dark:text-zinc-100">{t('reports.title')}</h1>
          <p className="text-ink-muted mt-1">{t('reports.subtitle')}</p>
        </div>

        {/* Filters Bar */}
        <div className="flex flex-col md:flex-row gap-3 sm:gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('common.search') + "..."}
              className="pl-10"
              data-testid="reports-search"
            />
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Select value={jobFilter} onValueChange={setJobFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="job-filter">
                <SelectValue placeholder={t('reports.filterByJob')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('reports.allJobs')}</SelectItem>
                {jobs.map(job => (
                  <SelectItem key={job.job_id} value={job.job_id}>
                    {job.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={verdictFilter} onValueChange={setVerdictFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="verdict-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder={t('reports.recommendation')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('reports.recommendation')}</SelectItem>
                <SelectItem value="go">{t('reports.go')}</SelectItem>
                <SelectItem value="evaluate">{t('reports.evaluate')}</SelectItem>
                <SelectItem value="no_go">{t('reports.noGo')}</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-52" data-testid="sort-filter">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder={t('reports.sortBy')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date_desc">{t('reports.sortDate')} ({t('reports.sortNewest', 'Plus récent')})</SelectItem>
                <SelectItem value="date_asc">{t('reports.sortDate')} ({t('reports.sortOldest', 'Plus ancien')})</SelectItem>
                <SelectItem value="score_desc">{t('reports.sortScore')} ({t('reports.sortHighest', 'Plus élevé')})</SelectItem>
                <SelectItem value="score_asc">{t('reports.sortScore')} ({t('reports.sortLowest', 'Plus bas')})</SelectItem>
                <SelectItem value="name_asc">{t('reports.sortName')} A-Z</SelectItem>
                <SelectItem value="job_asc">{t('reports.sortJob')} A-Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Results count */}
        {searchQuery || verdictFilter !== 'all' || jobFilter !== 'all' ? (
          <p className="text-sm text-ink-muted mb-4">
            {filteredReports.length} {t('common.viewMore').toLowerCase()}
          </p>
        ) : null}

        {/* Reports List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 skeleton rounded-sm" />
            ))}
          </div>
        ) : filteredReports.length === 0 ? (
          <Card className="card-flat">
            <CardContent className="py-16 text-center">
              <FileBarChart className="w-12 h-12 text-ink-muted mx-auto mb-4" />
              <h3 className="font-display font-semibold text-xl text-ink mb-2">
                {reports.length === 0 ? t('reports.noReports') : t('errors.notFound')}
              </h3>
              <p className="text-ink-muted">
                {reports.length === 0 
                  ? t('reports.noReportsDesc')
                  : t('errors.notFound')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="card-flat overflow-hidden">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('candidates.table.name')}</TableHead>
                  <TableHead className="hidden sm:table-cell">{t('candidates.table.job')}</TableHead>
                  <TableHead>{t('candidates.table.score')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('reports.recommendation')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('common.date')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.map((report) => (
                  <TableRow key={report.session_id} className="table-row-hover" data-testid={`report-row-${report.session_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-brand-secondary rounded-full flex items-center justify-center text-white font-medium">
                          {report.candidate?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-ink">{report.candidate?.name || 'N/A'}</p>
                          <p className="text-xs text-ink-muted">{report.candidate?.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm hidden sm:block">{report.job?.title || 'N/A'}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <span className={`font-mono-score font-bold text-lg ${getScoreColor(report.overall_score)}`}>
                          {report.overall_score ? Math.round(report.overall_score) : '-'}%
                        </span>
                        <div className="w-20 h-2 bg-zinc-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${getScoreBarColor(report.overall_score)}`}
                            style={{ width: `${report.overall_score || 0}%` }}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {report.recommendation ? (
                        <Badge 
                          variant="outline"
                          className={
                            report.recommendation === 'go' 
                              ? 'bg-functional-success/10 text-functional-success border-functional-success/30'
                              : report.recommendation === 'evaluate'
                                ? 'bg-yellow-100 text-yellow-600 border-yellow-300'
                                : 'bg-functional-error/10 text-functional-error border-functional-error/30'
                          }
                        >
                          {report.recommendation === 'go' ? (
                            <><CheckCircle2 className="w-3 h-3 mr-1" /> GO</>
                          ) : report.recommendation === 'evaluate' ? (
                            <><AlertCircle className="w-3 h-3 mr-1" /> {t('reports.evaluate')}</>
                          ) : (
                            <><XCircle className="w-3 h-3 mr-1" /> NO GO</>
                          )}
                        </Badge>
                      ) : (
                        <span className="text-ink-muted">-</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center gap-2 text-sm text-ink-muted">
                        <Calendar className="w-4 h-4" />
                        {report.completed_at 
                          ? new Date(report.completed_at).toLocaleDateString('fr-FR')
                          : '-'
                        }
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewReport(report.session_id)}
                          data-testid={`view-report-${report.session_id}`}
                        >
                          <Eye className="w-4 h-4 sm:mr-2" />
                          <span className="hidden sm:inline">{t('reports.viewDetails')}</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadPdf(report.session_id)}
                          data-testid={`download-report-${report.session_id}`}
                        >
                          <FileDown className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </Card>
        )}

        {/* Report Detail Dialog */}
        <Dialog open={!!selectedReport} onOpenChange={() => {
          setSelectedReport(null);
          setDetailData(null);
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="font-display text-xl">
                  {t('reports.title')}
                </DialogTitle>
                {selectedReport && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadPdf(selectedReport)}
                    data-testid="download-pdf-btn"
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    {t('reports.downloadPdf')}
                  </Button>
                )}
              </div>
            </DialogHeader>

            {detailLoading ? (
              <div className="py-12 text-center">
                <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-ink-muted">{t('common.loading')}</p>
              </div>
            ) : detailData ? (
              <div className="space-y-6 py-4">
                {/* Candidate Info */}
                <div className="flex items-center gap-4 p-4 bg-canvas-paper rounded-lg">
                  <div className="w-14 h-14 bg-brand-secondary rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {detailData.candidate?.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-ink">{detailData.candidate?.name}</h3>
                    <p className="text-sm text-ink-muted">{detailData.candidate?.email}</p>
                    <p className="text-sm text-ink-muted">{detailData.job?.title}</p>
                  </div>
                </div>

                {/* Scores - All on /100% scale */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Card className="card-flat">
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-ink-muted mb-1">{t('reports.scores.overall')}</p>
                      <p className={`font-mono-score font-bold text-2xl ${getScoreColor(
                        Math.round(((detailData.session?.hard_skills_score || 0) + (detailData.session?.soft_skills_score || 0) + (detailData.session?.vibe_score || 0)) / 3)
                      )}`}>
                        {Math.round(((detailData.session?.hard_skills_score || 0) + (detailData.session?.soft_skills_score || 0) + (detailData.session?.vibe_score || 0)) / 3)}%
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="card-flat">
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-ink-muted mb-1">{t('reports.scores.hardSkills')}</p>
                      <p className={`font-mono-score font-bold text-2xl ${getScoreColor(detailData.session?.hard_skills_score)}`}>
                        {Math.round(detailData.session?.hard_skills_score || 0)}%
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="card-flat">
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-ink-muted mb-1">{t('reports.scores.softSkills')}</p>
                      <p className={`font-mono-score font-bold text-2xl ${getScoreColor(detailData.session?.soft_skills_score)}`}>
                        {Math.round(detailData.session?.soft_skills_score || 0)}%
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="card-flat">
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-ink-muted mb-1">{t('reports.scores.vibeCheck', 'Vibe Check')}</p>
                      <p className={`font-mono-score font-bold text-2xl ${getScoreColor(detailData.session?.vibe_score)}`}>
                        {Math.round(detailData.session?.vibe_score || 0)}%
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Recommendation - Match verdict from session */}
                <div className={`p-4 rounded-lg text-center ${
                  (detailData.session?.recommendation === 'go' || detailData.session?.verdict === 'GREEN')
                    ? 'bg-functional-success/10'
                    : (detailData.session?.verdict === 'YELLOW' ? 'bg-yellow-100' : 'bg-functional-error/10')
                }`}>
                  <p className="text-sm mb-2">{t('reports.recommendation')}</p>
                  <div className="flex items-center justify-center gap-2">
                    {(detailData.session?.recommendation === 'go' || detailData.session?.verdict === 'GREEN') ? (
                      <>
                        <CheckCircle2 className="w-6 h-6 text-functional-success" />
                        <span className="font-display font-bold text-xl text-functional-success">GO</span>
                      </>
                    ) : detailData.session?.verdict === 'YELLOW' ? (
                      <>
                        <AlertCircle className="w-6 h-6 text-yellow-500" />
                        <span className="font-display font-bold text-xl text-yellow-500">À ÉVALUER</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-6 h-6 text-functional-error" />
                        <span className="font-display font-bold text-xl text-functional-error">NO GO</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Video Section */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Video className="w-5 h-5 text-brand-primary" />
                    <h4 className="font-semibold text-ink">{t('reports.interviewVideo', 'Vidéo de l\'entretien')}</h4>
                    <Badge variant="outline" className="text-xs">{t('reports.videoDuration', '~2 min')}</Badge>
                  </div>
                  
                  {videoLoading ? (
                    <div className="bg-canvas-paper rounded-lg p-8 text-center">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-brand-primary" />
                      <p className="text-sm text-ink-muted">{t('common.loading')}</p>
                    </div>
                  ) : videoUrl ? (
                    <div className="bg-slate-900 rounded-lg overflow-hidden">
                      <video 
                        src={videoUrl}
                        controls
                        className="w-full max-h-[300px]"
                        poster="/video-poster.png"
                        data-testid="interview-video"
                      >
                        {t('reports.videoNotSupported', 'Votre navigateur ne supporte pas la lecture vidéo.')}
                      </video>
                      <div className="p-3 bg-slate-800 flex items-center justify-between">
                        <p className="text-xs text-slate-400">
                          {t('reports.videoRecorded', 'Enregistrement automatique de l\'entretien')}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-300 hover:text-white"
                          onClick={() => window.open(videoUrl, '_blank')}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          {t('common.download')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-canvas-paper rounded-lg p-6 text-center border-2 border-dashed border-zinc-200">
                      {detailData.session?.video_status === 'processing' ? (
                        <>
                          <Loader2 className="w-10 h-10 mx-auto mb-2 text-indigo-500 animate-spin" />
                          <p className="text-sm font-medium text-indigo-600">Vidéo en cours de traitement...</p>
                          <p className="text-xs text-ink-muted mt-1">Elle apparaîtra ici dans quelques instants.</p>
                        </>
                      ) : (
                        <>
                          <Video className="w-10 h-10 mx-auto mb-2 text-ink-muted opacity-50" />
                          <p className="text-sm text-ink-muted">
                            {t('reports.noVideo', 'Aucune vidéo disponible pour cet entretien')}
                          </p>
                          <p className="text-xs text-ink-muted mt-1">
                            {t('reports.videoNote', 'La vidéo est disponible uniquement pour les entretiens récents')}
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Strengths and Red Flags - Match PDF */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-functional-success mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      {t('reports.strengths', 'Points forts')}
                    </h4>
                    <ul className="space-y-1">
                      {(detailData.session?.strengths || []).length > 0 ? (
                        detailData.session.strengths.map((s, i) => (
                          <li key={i} className="text-sm text-ink-muted flex items-start gap-2">
                            <span className="text-functional-success">•</span> {s}
                          </li>
                        ))
                      ) : (
                        <li className="text-sm text-ink-muted italic">{t('reports.noStrengths', 'Aucun point fort identifié')}</li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-functional-error mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {t('reports.redFlags', 'Points d\'attention')}
                    </h4>
                    <ul className="space-y-1">
                      {(detailData.session?.red_flags || []).length > 0 ? (
                        detailData.session.red_flags.map((f, i) => (
                          <li key={i} className="text-sm text-ink-muted flex items-start gap-2">
                            <span className="text-functional-error">•</span> {f}
                          </li>
                        ))
                      ) : (
                        <li className="text-sm text-ink-muted italic">{t('reports.noRedFlags', 'Aucun signalement')}</li>
                      )}
                    </ul>
                  </div>
                </div>

                {/* Analysis - Show detailed breakdowns */}
                {(detailData.session?.analysis || detailData.session?.hard_skills_analysis) && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-ink mb-2">{t('reports.analysis')}</h4>
                    
                    {detailData.session?.overall_assessment && (
                      <p className="text-sm text-ink-muted bg-canvas-paper p-4 rounded-lg">
                        {detailData.session.overall_assessment}
                      </p>
                    )}
                    
                    {!detailData.session?.overall_assessment && detailData.session?.analysis && 
                     !detailData.session.analysis.toLowerCase().includes('score global:') && (
                      <p className="text-sm text-ink-muted bg-canvas-paper p-4 rounded-lg">
                        {detailData.session.analysis}
                      </p>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {detailData.session?.hard_skills_analysis && (
                        <div className="bg-canvas-paper p-3 rounded-lg">
                          <p className="text-xs font-semibold text-ink mb-1">{t('reports.hardSkills', 'Hard Skills')}</p>
                          <p className="text-xs text-ink-muted">{detailData.session.hard_skills_analysis}</p>
                        </div>
                      )}
                      {detailData.session?.soft_skills_analysis && (
                        <div className="bg-canvas-paper p-3 rounded-lg">
                          <p className="text-xs font-semibold text-ink mb-1">{t('reports.softSkills', 'Soft Skills')}</p>
                          <p className="text-xs text-ink-muted">{detailData.session.soft_skills_analysis}</p>
                        </div>
                      )}
                      {detailData.session?.vibe_analysis && (
                        <div className="bg-canvas-paper p-3 rounded-lg">
                          <p className="text-xs font-semibold text-ink mb-1">{t('reports.vibeScore', 'Impression')}</p>
                          <p className="text-xs text-ink-muted">{detailData.session.vibe_analysis}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Weaknesses & Improvement Areas */}
                {((detailData.session?.weaknesses || []).length > 0 || (detailData.session?.improvement_areas || []).length > 0) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(detailData.session?.weaknesses || []).length > 0 && (
                      <div>
                        <h4 className="font-semibold text-amber-600 dark:text-amber-400 mb-2 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          {t('reports.weaknesses', 'Faiblesses')}
                        </h4>
                        <ul className="space-y-1">
                          {detailData.session.weaknesses.map((w, i) => (
                            <li key={i} className="text-sm text-ink-muted flex items-start gap-2">
                              <span className="text-amber-500">•</span> {w}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(detailData.session?.improvement_areas || []).length > 0 && (
                      <div>
                        <h4 className="font-semibold text-blue-600 dark:text-blue-400 mb-2 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" />
                          {t('reports.improvementAreas', "Axes d'amélioration")}
                        </h4>
                        <ul className="space-y-1">
                          {detailData.session.improvement_areas.map((a, i) => (
                            <li key={i} className="text-sm text-ink-muted flex items-start gap-2">
                              <span className="text-blue-500">•</span> {a}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Q&A */}
                {detailData.session?.responses?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-ink mb-3">{t('reports.questionsAnswers')}</h4>
                    <div className="space-y-4">
                      {detailData.session.responses.map((item, index) => (
                        <div key={index} className="border-l-2 border-brand-accent pl-4">
                          <p className="text-sm font-medium text-ink mb-1">
                            Q{index + 1}: {item.question}
                          </p>
                          <p className="text-sm text-ink-muted">
                            R: {item.response_text || '-'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
